import React, { useState } from 'react';
import { ArrowRight, Home, TrendingUp, Shield, RefreshCw, Calculator, PiggyBank, Star, Mail, Phone, MapPin, Menu, X, Layers, Users, Hammer, DollarSign, Building, Building2, FileText, Truck, Store, Briefcase, Paintbrush } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { LoanCalculator } from './components/LoanCalculator';
import { NegativeGearingCalculator } from './components/NegativeGearingCalculator';
import { whyWorkWithMe, residentialServices, commercialServices, taxStrategies, faqs, whyChooseUs, submitContactForm, captureCalculatorLead } from './data/mock';
import { toast } from 'sonner';

const iconMap = {
  Home,
  TrendingUp,
  Shield,
  RefreshCw,
  Calculator,
  PiggyBank,
  Star,
  Target: TrendingUp,
  Building,
  Building2,
  Heart: Star,
  Layers,
  Users,
  Hammer,
  DollarSign,
  FileText,
  Truck,
  Store,
  Briefcase,
  Paintbrush
};

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success(result.message, {
          duration: 5000
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try emailing us directly at joshua@jcmcapital.com.au');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-md border-b border-[#D4AF37]/20 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_debt-freedom-50/artifacts/521bgjyn_jcm_capital_transparent.png"
                alt="JCM Capital Logo"
                className="h-12 sm:h- md:h- lg:h-18 w-auto" />

            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium">
                About
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('calculators')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium">
                Calculators
              </button>
              <button onClick={() => scrollToSection('tax-benefits')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium">
                Tax Benefits
              </button>
              <button onClick={() => scrollToSection('why-me')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium">
                Why Choose Me
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-[#D4AF37] hover:bg-[#C4A037] text-[#1a1a1a] font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">

                Get Started
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-gray-300 hover:text-[#D4AF37] transition-colors">

              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen &&
          <div className="md:hidden py-4 border-t border-[#D4AF37]/20">
              <nav className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium text-left">
                  About
                </button>
                <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium text-left">
                  Services
                </button>
                <button onClick={() => scrollToSection('calculators')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium text-left">
                  Calculators
                </button>
                <button onClick={() => scrollToSection('tax-benefits')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium text-left">
                  Tax Benefits
                </button>
                <button onClick={() => scrollToSection('why-me')} className="text-gray-300 hover:text-[#D4AF37] transition-colors font-medium text-left">
                  Why Choose Me
                </button>
                <Button
                onClick={() => scrollToSection('contact')}
                className="bg-[#D4AF37] hover:bg-[#C4A037] text-[#1a1a1a] font-semibold w-full">

                  Get Started
                </Button>
              </nav>
            </div>
          }
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob21lfGVufDB8fHx8MTc3Mzg0Mjg4OHww&ixlib=rb-4.1.0&q=85"
            alt="Luxury Property"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#1a1a1a]/85 to-[#1a1a1a]/70"></div>
        </div>

        <div className="container mx-auto px-4 z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Build Wealth Through </span>
              <span className="text-[#D4AF37]">Property</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Expert mortgage solutions designed to help you save on taxes, pay off loans faster, and create lasting wealth through strategic property investment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#C4A037] text-[#1a1a1a] text-lg px-8 py-6 font-semibold transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105">

                Start Your Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => scrollToSection('calculators')}
                size="lg"
                variant="outline"
                className="border-2 border-[#7A9B76] text-white hover:bg-[#7A9B76] hover:text-white text-lg px-8 py-6 font-semibold transition-all duration-300">

                Try Our Calculators
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-16">
              <div className="text-center">
                <p className="text-4xl font-bold text-[#D4AF37] mb-2">40+</p>
                <p className="text-gray-400 text-sm">Lender Partners</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#7A9B76] mb-2">100%</p>
                <p className="text-gray-400 text-sm">Personalized Service</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#D4AF37] mb-2">Free</p>
                <p className="text-gray-400 text-sm">Consultations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About / Why Choose Us Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-[#7A9B76]">JCM Capital</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not just mortgage brokers—we're your partners in building lasting wealth through smart property investment and strategic tax planning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const Icon = iconMap[item.icon] || Star;
              return (
                <Card key={item.id} className="border-2 border-transparent hover:border-[#7A9B76] transition-all duration-300 shadow-lg hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-[#7A9B76] to-[#6B8A67] rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Lender Partners Section - Text Carousel Above Services */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-lg text-gray-700 font-medium mb-2">
              We can access hundreds of loans from Australia's biggest brands
            </p>
          </div>

          {/* Infinite Scroll Text Carousel */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll">
              {/* First set of logos */}
              <div className="flex items-center gap-12 px-6 whitespace-nowrap">
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#007DBA]">ANZ</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#D1001F]">Westpac</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#FFCC00]">CBA</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#E30613]">NAB</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#E30613]">St.George</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#F68B1F]">Suncorp</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-gray-800">Macquarie</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#FF6600]">ING</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#662483]">Bendigo Bank</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-lg font-bold text-gray-800">Pepper Money</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#003DA5]">Liberty</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#E03C31]">ME Bank</span>
                </div>
              </div>
              
              {/* Duplicate set for seamless loop */}
              <div className="flex items-center gap-12 px-6 whitespace-nowrap">
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#007DBA]">ANZ</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#D1001F]">Westpac</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#FFCC00]">CBA</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-2xl font-bold text-[#E30613]">NAB</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#E30613]">St.George</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#F68B1F]">Suncorp</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-gray-800">Macquarie</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#FF6600]">ING</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#662483]">Bendigo Bank</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-lg font-bold text-gray-800">Pepper Money</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#003DA5]">Liberty</span>
                </div>
                <div className="flex items-center justify-center h-16 w-32 transition-all">
                  <span className="text-xl font-bold text-[#E03C31]">ME Bank</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">+ 40+ more lenders</p>
          </div>
        </div>
      </section>

      {/* Services Section - Horizontal Grid Layout */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#D4AF37]">Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive residential and commercial loan solutions for every stage of your property and business journey.
            </p>
          </div>

          <div className="max-w-7xl mx-auto space-y-10">
            {/* Residential Services - Grid */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#7A9B76] rounded-lg flex items-center justify-center">
                  <Home className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Residential Services</h3>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {residentialServices.map((service) => {
                  const Icon = iconMap[service.icon] || Home;
                  return (
                    <Accordion key={service.id} type="single" collapsible>
                      <AccordionItem
                        value={`residential-${service.id}`}
                        className="border-2 border-gray-200 hover:border-[#7A9B76] transition-all px-4 rounded-lg shadow-sm bg-white">

                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex flex-col items-center text-center gap-2 w-full">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#7A9B76] to-[#6B8A67] rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-semibold text-sm text-gray-900 leading-tight">{service.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-3 pb-4">
                          <p className="text-xs text-gray-600 mb-3 text-center leading-relaxed">{service.description}</p>
                          <ul className="space-y-1.5">
                            {service.features.map((feature, index) =>
                            <li key={index} className="flex items-start gap-1.5 text-xs text-gray-600">
                                <span className="text-[#7A9B76] mt-0.5 flex-shrink-0">•</span>
                                <span>{feature}</span>
                              </li>
                            )}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>);

                })}
              </div>
            </div>

            {/* Commercial Services - Grid */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <Building className="w-5 h-5 text-[#1a1a1a]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Commercial Services</h3>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {commercialServices.map((service) => {
                  const Icon = iconMap[service.icon] || Building;
                  return (
                    <Accordion key={service.id} type="single" collapsible>
                      <AccordionItem
                        value={`commercial-${service.id}`}
                        className="border-2 border-gray-200 rounded-lg px-4 bg-white hover:border-[#D4AF37] transition-all shadow-sm">

                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex flex-col items-center text-center gap-2 w-full">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#C4A037] rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-[#1a1a1a]" />
                            </div>
                            <span className="font-semibold text-sm text-gray-900 leading-tight">{service.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-3 pb-4">
                          <p className="text-xs text-gray-600 mb-3 text-center leading-relaxed">{service.description}</p>
                          <ul className="space-y-1.5">
                            {service.features.map((feature, index) =>
                            <li key={index} className="flex items-start gap-1.5 text-xs text-gray-600">
                                <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">•</span>
                                <span>{feature}</span>
                              </li>
                            )}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>);

                })}
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Card className="max-w-3xl mx-auto border-2 border-[#7A9B76]/30 bg-gradient-to-br from-[#7A9B76]/5 to-transparent shadow-lg">
              <CardContent className="p-6">
                <p className="text-base text-gray-700 mb-4">
                  Don't see what you're looking for? I have access to specialist lenders for unique situations and complex scenarios.
                </p>
                <Button
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#7A9B76] hover:bg-[#6B8A67] text-white px-8 py-3 font-semibold">

                  Discuss Your Needs
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section id="calculators" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-[#7A9B76]">Financial</span> Calculators
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Make informed decisions with our comprehensive calculators. See exactly how much you can save and the tax benefits available to you.
            </p>
          </div>

          <Tabs defaultValue="loan" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 h-auto p-1 bg-gray-200">
              <TabsTrigger
                value="loan"
                className="text-base py-3 data-[state=active]:bg-[#7A9B76] data-[state=active]:text-white transition-all">

                Loan Calculator
              </TabsTrigger>
              <TabsTrigger
                value="negative-gearing"
                className="text-base py-3 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1a1a1a] transition-all">

                Negative Gearing
              </TabsTrigger>
            </TabsList>

            <TabsContent value="loan" className="mt-0">
              <LoanCalculator />
            </TabsContent>

            <TabsContent value="negative-gearing" className="mt-0">
              <NegativeGearingCalculator />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Tax Benefits Section */}
      <section id="tax-benefits" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBwbGFubmluZ3xlbnwwfHx8fDE3NzM3MzI1ODF8MA&ixlib=rb-4.1.0&q=85"
            alt="Financial Planning Background"
            className="w-full h-full object-cover" />

        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Maximize Your <span className="text-[#D4AF37]">Tax Benefits</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Smart tax strategies that keep more money in your pocket while accelerating your wealth-building journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {taxStrategies.map((strategy) => {
              const Icon = iconMap[strategy.icon] || Calculator;
              return (
                <Card key={strategy.id} className="border-2 border-gray-200 hover:border-[#7A9B76] transition-all duration-300 shadow-lg hover:shadow-xl">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#C4A037] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#1a1a1a]" />
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">{strategy.title}</CardTitle>
                        <CardDescription className="text-base">{strategy.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] rounded-r-lg p-4">
                      <p className="text-sm text-gray-700 leading-relaxed">{strategy.tip}</p>
                    </div>
                  </CardContent>
                </Card>);

            })}
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Frequently Asked <span className="text-[#7A9B76]">Questions</span>
            </h3>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) =>
              <AccordionItem
                key={faq.id}
                value={`faq-${faq.id}`}
                className="border-2 border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-all">

                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Work With Me Section */}
      <section id="why-me" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Work <span className="text-[#7A9B76]">With Me</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As your dedicated mortgage broker, I bring fresh energy, personalized attention, and a genuine commitment to helping you build wealth through property.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {whyWorkWithMe.map((item) => {
              const Icon = iconMap[item.icon] || Star;
              return (
                <Card key={item.id} className="border-2 border-transparent hover:border-[#D4AF37] transition-all duration-300 shadow-lg hover:shadow-xl">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#7A9B76] to-[#6B8A67] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-3">{item.title}</CardTitle>
                        <CardDescription className="text-base text-gray-700 leading-relaxed">
                          {item.content}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>);

            })}
          </div>

          <div className="mt-16 max-w-4xl mx-auto">
            <Card className="border-2 border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/5 to-transparent shadow-xl">
              <CardContent className="p-8">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Start Your Property Journey?</h3>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    Let's have a conversation about your goals. Whether you're buying your first home, building an investment portfolio, or exploring SMSF options, I'm here to help you make informed decisions.
                  </p>
                  <Button
                    onClick={() => scrollToSection('contact')}
                    size="lg"
                    className="bg-[#7A9B76] hover:bg-[#6B8A67] text-white text-lg px-8 py-6 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">

                    Book Your Free Consultation
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Let's Start Building Your <span className="text-[#D4AF37]">Wealth</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get in touch today for a free consultation. Let's discuss how we can help you achieve your property investment goals.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <Card className="border-2 border-[#7A9B76]/30 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a message</CardTitle>
                  <CardDescription>We'll get back to you within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Smith"
                        className="h-12" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@example.com"
                        className="h-12" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="0400 000 000"
                        className="h-12" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us about your property goals..."
                        rows={5}
                        className="resize-none" />

                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#7A9B76] hover:bg-[#6B8A67] text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">

                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className="space-y-8">
                <Card className="border-2 border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Phone</p>
                        <p className="text-gray-600">0450 546 493</p>
                        <p className="text-gray-600">Available Mon-Fri, 9am-6pm</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#7A9B76]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#7A9B76]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Email</p>
                        <p className="text-gray-600">joshua@jcmcapital.com.au</p>
                        <p className="text-gray-600">We respond within 24 hours</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Location</p>
                        <p className="text-gray-600">Sydney, NSW</p>
                        <p className="text-gray-600">Serving clients Australia-wide</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-[#7A9B76]/30 bg-gradient-to-br from-[#7A9B76]/5 to-transparent shadow-lg">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calculator className="w-6 h-6 text-[#1a1a1a]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-2 text-lg">Free Consultation</p>
                        <p className="text-gray-700 leading-relaxed">
                          Book a free 30-minute consultation to discuss your property goals and discover how much you could save in taxes.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-gray-300 py-12 border-t border-[#D4AF37]/20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_debt-freedom-50/artifacts/521bgjyn_jcm_capital_transparent.png"
                alt="JCM Capital Logo"
                className="h-16 mb-4" />

              <p className="text-gray-400 leading-relaxed">
                Building wealth through property. Expert mortgage solutions with a focus on tax optimization and accelerated loan repayment.
              </p>
            </div>

            <div>
              <h3 className="text-[#D4AF37] font-semibold text-lg mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-[#D4AF37] transition-colors">
                  About Us
                </button>
                <button onClick={() => scrollToSection('services')} className="block text-gray-400 hover:text-[#D4AF37] transition-colors">
                  Services
                </button>
                <button onClick={() => scrollToSection('calculators')} className="block text-gray-400 hover:text-[#D4AF37] transition-colors">
                  Calculators
                </button>
                <button onClick={() => scrollToSection('why-me')} className="block text-gray-400 hover:text-[#D4AF37] transition-colors">
                  Why Choose Me
                </button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-[#D4AF37] transition-colors">
                  Contact
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-[#D4AF37] font-semibold text-lg mb-4">Services</h3>
              <div className="space-y-2">
                <p className="text-gray-400">Owner Occupied Loans</p>
                <p className="text-gray-400">Investment Property Loans</p>
                <p className="text-gray-400">SMSF Lending</p>
                <p className="text-gray-400">Refinancing Solutions</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} JCM Capital. All rights reserved. | Australian Credit License Representative
            </p>
            <p className="text-gray-500 text-xs mt-2">
              This website provides general information only. Please seek professional advice for your specific situation.
            </p>
          </div>
        </div>
      </footer>
    </div>);

}

export default App;